import React, { FC, useState } from 'react';

import { Box, Typography, Button, RadioGroup, FormControlLabel, Radio } from '@mui/material';

import { PublishNotificationMailType } from '@hooks_iqfu/dashboard/usePublishNotification';

type NotificationStep = 'select' | 'confirm';

type Props = {
  onClose: () => void;
  onConfirm: (notificationType: Exclude<PublishNotificationMailType, 'general'>) => void;
};

const PublishNotificationDialog: FC<Props> = ({ onClose, onConfirm }) => {
  const [currentStep, setCurrentStep] = useState<NotificationStep>('select');
  const [selectedType, setSelectedType] =
    useState<Exclude<PublishNotificationMailType, 'general'>>('template_published');

  const handleNext = () => {
    setCurrentStep('confirm');
  };

  const handleBack = () => {
    setCurrentStep('select');
  };

  const handleConfirm = () => {
    onConfirm(selectedType);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setCurrentStep('select');
      setSelectedType('template_published');
    }, 300);
  };

  const notificationTypeLabels: Record<Exclude<PublishNotificationMailType, 'general'>, string> = {
    template_published: '定型文公開済み',
    profile_completed: '会社紹介文完成公開済み',
    not_request_link: '相互リンク依頼なし',
  };

  return (
    <Box
      sx={{
        padding: '48px',
        textAlign: 'center',
        minWidth: '400px',
      }}
    >
      {currentStep === 'select' && (
        <>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              mb: 5,
            }}
          >
            通知タイプを選択
          </Typography>

          <RadioGroup
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as Exclude<PublishNotificationMailType, 'general'>)}
            row
            sx={{
              justifyContent: 'center',
              mb: 5,
              '& .MuiFormControlLabel-root': {
                mx: 3,
                '& .MuiFormControlLabel-label': {
                  fontSize: '1.6rem',
                  minWidth: '100px',
                  padding: '6px 20px 6px 44px',
                  position: 'relative',
                },
                '& .MuiRadio-root': {
                  position: 'absolute',
                  marginTop: '-2px',
                  color: '#ff6600',
                  '& .MuiSvgIcon-root': {
                    width: '24px',
                    height: '24px',
                  },
                  '&.Mui-checked': {
                    color: '#ff6600',
                  },
                },
              },
            }}
          >
            <FormControlLabel value="template_published" control={<Radio />} label="定型文公開済み" />
            <FormControlLabel value="profile_completed" control={<Radio />} label="会社紹介文完成公開済み" />
            <FormControlLabel value="not_request_link" control={<Radio />} label="相互リンク依頼なし" />
          </RadioGroup>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                width: '300px',
                padding: '1em',
                fontSize: '2rem',
                fontWeight: 'bold',
                lineHeight: 1,
              }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                width: '300px',
                padding: '1em',
                fontSize: '2rem',
                fontWeight: 'bold',
                lineHeight: 1,
                bgcolor: '#28a745',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#218838',
                },
              }}
            >
              作成
            </Button>
          </Box>
        </>
      )}

      {currentStep === 'confirm' && (
        <>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            下記内容で公開お知らせメールを送信しますか？
          </Typography>

          <Typography
            sx={{
              mb: 2,
              fontSize: '1.6rem',
              fontWeight: 'bold',
            }}
          >
            {notificationTypeLabels[selectedType]}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{
                width: '300px',
                padding: '1em',
                fontSize: '2rem',
                fontWeight: 'bold',
                lineHeight: 1,
              }}
            >
              戻る
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              sx={{
                width: '300px',
                padding: '1em',
                fontSize: '2rem',
                fontWeight: 'bold',
                lineHeight: 1,
                bgcolor: '#28a745',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#218838',
                },
              }}
            >
              送信
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PublishNotificationDialog;
